import React from 'react';
import {
  AbsoluteFill,
  interpolate,
  interpolateColors,
  spring,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from 'remotion';

// --- HELPER COMPONENTS ---

const GridBackground = () => {
  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#f8fafc',
        backgroundImage: `linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)`,
        backgroundSize: '48px 48px',
        opacity: 0.4,
      }}
    />
  );
};

const Icons = {
  Plus: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Logout: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  ChevronUp: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Play: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  Music: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  ),
  ExternalLink: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  Check: () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
};

const Cursor = ({ x, y, frame }: { x: number; y: number; frame: number }) => {
  // Click animation frames
  const clickFrames = [55, 110, 190, 250];
  const isClicking = clickFrames.some(f => Math.abs(frame - f) < 5);
  const clickScale = isClicking ? 0.8 : 1;

  // Ripple effect on click
  const rippleFrame = clickFrames.find(f => frame >= f && frame < f + 20);
  const rippleProgress = rippleFrame ? (frame - rippleFrame) / 20 : 0;
  const rippleScale = interpolate(rippleProgress, [0, 1], [0, 3], { extrapolateRight: 'clamp' });
  const rippleOpacity = interpolate(rippleProgress, [0, 0.5, 1], [0, 0.4, 0], { extrapolateRight: 'clamp' });

  return (
    <div style={{
      position: 'absolute',
      left: x,
      top: y,
      zIndex: 1000,
      pointerEvents: 'none',
      transform: `scale(${clickScale})`,
      transition: 'transform 0.1s ease-out',
    }}>
      {rippleFrame && (
        <div style={{
          position: 'absolute',
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: '2px solid #0f172a',
          transform: `translate(-50%, -50%) scale(${rippleScale})`,
          opacity: rippleOpacity,
        }} />
      )}
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
        <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="white" stroke="#0f172a" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const Character = ({ frame }: { frame: number }) => {
  const bob = Math.sin(frame * 0.1) * 8;
  const sway = Math.sin(frame * 0.08) * 1.5;

  return (
    <div style={{
      transform: `translateY(${bob}px) rotate(${sway}deg)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{
        width: 100,
        height: 120,
        backgroundColor: '#fde047',
        border: '3px solid #854d0e',
        borderRadius: 8,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ display: 'flex', gap: 10, marginTop: -20 }}>
          {[0, 1].map((i) => (
            <div key={i} style={{
              width: 34,
              height: 34,
              backgroundColor: 'white',
              border: '2px solid #854d0e',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{ width: 14, height: 14, backgroundColor: '#22c55e', borderRadius: '50%', border: '1px solid #166534' }} />
            </div>
          ))}
        </div>
        <div style={{ width: 40, height: 2, backgroundColor: '#854d0e', marginTop: 15, position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 8, width: 8, height: 10, backgroundColor: 'white', border: '2px solid #854d0e', borderTop: 'none' }} />
          <div style={{ position: 'absolute', top: 0, right: 8, width: 8, height: 10, backgroundColor: 'white', border: '2px solid #854d0e', borderTop: 'none' }} />
        </div>
        <div style={{ position: 'absolute', bottom: 0, width: '100%', height: 30, backgroundColor: '#fff', borderTop: '3px solid #854d0e', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '12px solid #ef4444', marginTop: 2 }} />
          <div style={{ position: 'absolute', bottom: -10, width: '100%', height: 15, backgroundColor: '#713f12', borderTop: '3px solid #854d0e' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 30 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ width: 4, height: 20, backgroundColor: '#854d0e', position: 'relative' }}>
            <div style={{ position: 'absolute', bottom: -4, left: -4, width: 16, height: 8, backgroundColor: '#000', borderRadius: '4px 4px 0 0' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

const PreviewCanvas = () => {
  const frame = useCurrentFrame();
  return (
    <div style={{
      flex: 1,
      backgroundColor: '#0d9488',
      borderRadius: 12,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {[
        { t: 20, l: 15, s: 120 },
        { t: 50, l: 70, s: 180 },
        { t: 15, l: 60, s: 90 },
      ].map((b, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: `${b.t}%`,
          left: `${b.l}%`,
          width: b.s,
          height: b.s * 0.7,
          backgroundColor: '#5eead4',
          opacity: 0.3,
          borderRadius: '50%',
          filter: 'blur(20px)',
          transform: `translateX(${Math.sin(frame * 0.05 + i) * 5}px)`,
        }} />
      ))}
      <div style={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        height: '22%',
        backgroundColor: '#facc15',
        borderRadius: '50% 50% 0 0 / 20% 20% 0 0',
        transform: 'scaleX(1.2)',
      }} />
      <div style={{ transform: 'translateX(40px)' }}>
        <Character frame={frame} />
      </div>
    </div>
  );
};

const SettingTile = ({ label, ratio, selected = false }: { label: string, ratio: string, selected?: boolean }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const selectionSpring = spring({
    frame: frame - 40,
    fps,
    config: { damping: 12 },
  });

  const borderColor = selected 
    ? interpolateColors(selectionSpring, [0, 1], ['#e2e8f0', '#0b1220'])
    : '#e2e8f0';
  
  const borderWidth = selected ? interpolate(selectionSpring, [0, 1], [1, 2.5]) : 1;
  const badgeScale = selected ? interpolate(selectionSpring, [0, 1], [0, 1]) : 0;

  return (
    <div style={{
      backgroundColor: 'white',
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius: 16,
      padding: '8px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      position: 'relative',
      overflow: 'visible',
    }}>
      <div style={{ height: 40, backgroundColor: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 20, height: 12, border: '1.5px solid #cbd5e1', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 5, height: 5, backgroundColor: '#cbd5e1', borderRadius: '50%' }} />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>{ratio}</div>
      </div>
      {selected && (
        <div style={{
          position: 'absolute',
          top: -6,
          right: -6,
          width: 18,
          height: 18,
          backgroundColor: '#0b1220',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `scale(${badgeScale})`,
          border: '2px solid white',
        }}>
          <Icons.Check />
        </div>
      )}
    </div>
  );
};

// --- MAIN EXPORTED COMPONENT ---

export const VemotionRecreation = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // Mouse path coordinates
  const mouseX = interpolate(frame, 
    [0, 50, 55, 100, 110, 180, 190, 240, 250, 300], 
    [width + 50, 240, 240, 120, 120, 260, 260, 1850, 1850, 1200], 
    { easing: Easing.bezier(0.42, 0, 0.58, 1) }
  );
  const mouseY = interpolate(frame, 
    [0, 50, 55, 100, 110, 180, 190, 240, 250, 300], 
    [height + 50, 32, 32, 165, 165, 755, 755, 780, 780, 350], 
    { easing: Easing.bezier(0.42, 0, 0.58, 1) }
  );

  const entrySpring = spring({
    frame,
    fps,
    config: { damping: 15 },
  });

  const translateY = interpolate(entrySpring, [0, 1], [15, 0]);
  const opacity = interpolate(entrySpring, [0, 1], [0, 1]);

  const btnSpring = spring({
    frame: frame - 185, // Linked to mouse click on Generate
    fps,
    config: { stiffness: 200, damping: 10 },
  });
  const btnScale = interpolate(btnSpring, [0, 0.5, 1], [1, 0.95, 1]);

  const sidebarWidth = width * 0.27;
  const mainWidth = width - sidebarWidth;

  const textStyle: React.CSSProperties = {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#0f172a',
  };

  return (
    <AbsoluteFill style={{ ...textStyle, backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <GridBackground />
      
      {/* Dynamic Cursor Overlay */}
      <Cursor x={mouseX} y={mouseY} frame={frame} />

      <div style={{ opacity, transform: `translateY(${translateY}px)`, display: 'flex', flexDirection: 'column', height: '100%' }}>
        
        {/* Top App Bar */}
        <div style={{
          height: height * 0.06,
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <span style={{ fontWeight: 700, fontSize: 19, color: '#0f172a' }}>Vemotion AI</span>
            <div style={{
              padding: '6px 14px',
              border: '1px solid #e2e8f0',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              color: '#334155',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              backgroundColor: 'white',
              transform: frame > 50 && frame < 65 ? 'scale(0.97)' : 'scale(1)',
              transition: 'transform 0.1s'
            }}>
              <Icons.Plus /> New Video
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 14 }}>
            <Icons.Logout /> Logout
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1 }}>
          
          {/* Left Sidebar */}
          <div style={{ 
            width: sidebarWidth, 
            borderRight: '1px solid #e2e8f0', 
            padding: '16px 20px', 
            overflowY: 'hidden', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 12 
          }}>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 2px 0' }}>What do you want to create?</h2>
              <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Describe your video animation</p>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ 
                padding: '5px 14px', 
                borderRadius: 20, 
                border: '1px solid #e2e8f0', 
                backgroundColor: 'white', 
                fontSize: 12, 
                fontWeight: 600, 
                color: '#0f172a',
                transform: frame > 105 && frame < 120 ? 'scale(0.95)' : 'scale(1)',
                transition: 'transform 0.1s'
              }}>Describe</div>
              <div style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid #e2e8f0', backgroundColor: 'white', fontSize: 12, fontWeight: 500, color: '#94a3b8' }}>From Image</div>
            </div>

            <div style={{ 
              backgroundColor: 'white', 
              border: '1px solid #e2e8f0', 
              borderRadius: 16, 
              padding: '12px 16px', 
              height: 80, 
              position: 'relative',
              fontSize: 14,
              color: '#334155'
            }}>
              green eyes
              <div style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                width: 28,
                height: 28,
                backgroundColor: '#f59e0b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{ width: 10, height: 10, border: '2px solid white', borderRadius: '50%' }} />
              </div>
            </div>

            {/* Video Settings Panel */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 20,
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>Video settings</span>
                <Icons.ChevronUp />
              </div>

              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>Where will you use this?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <SettingTile label="Default" ratio="16:9" selected />
                  <SettingTile label="Hero Section" ratio="12:5" />
                  <SettingTile label="Wide Banner" ratio="24:5" />
                  <SettingTile label="Feature Card" ratio="1:1" />
                  <SettingTile label="Sidebar" ratio="2:3" />
                  <SettingTile label="Inline Content" ratio="4:3" />
                </div>
                <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 8 }}>1920 × 1080 px</div>
              </div>

              <div style={{ height: 1, backgroundColor: '#e2e8f0' }} />

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>FPS</div>
                  <div style={{ height: 44, border: '1px solid #e2e8f0', borderRadius: 12, padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14 }}>
                    30 <Icons.ChevronDown />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Duration (sec)</div>
                  <div style={{ height: 44, border: '1px solid #e2e8f0', borderRadius: 12, padding: '0 12px', display: 'flex', alignItems: 'center', fontSize: 14 }}>
                    10
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div style={{
              height: 52,
              backgroundColor: '#0f172a',
              borderRadius: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'white',
              fontWeight: 600,
              fontSize: 15,
              transform: `scale(${btnScale})`,
              cursor: 'pointer',
              flexShrink: 0,
            }}>
              <Icons.Play /> Generate
            </div>

            {/* Usage Section */}
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Usage</div>
              {[
                { label: 'Videos', val: '38 left (12/50)', p: 24 },
                { label: 'Tokens used', val: '9 left (1/10)', p: 10 }
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>{item.label}</span>
                    <span style={{ color: '#334155', fontWeight: 500 }}>{item.val}</span>
                  </div>
                  <div style={{ height: 5, backgroundColor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${item.p}%`, height: '100%', backgroundColor: '#0f172a' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div style={{ width: mainWidth, padding: '32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ 
              backgroundColor: 'white', 
              border: '1px solid #e2e8f0', 
              borderRadius: 20, 
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              maxHeight: '52%'
            }}>
              <PreviewCanvas />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 4px 0 4px', fontSize: 12, color: '#64748b' }}>
                <span>37456ms • 6,317 tokens</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>3 sources <Icons.ExternalLink /></span>
              </div>
            </div>

            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 20, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>Version History</span>
                <span style={{ fontSize: 12, backgroundColor: '#f1f5f9', padding: '2px 8px', borderRadius: 10, color: '#64748b' }}>2 versions</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#0f172a' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>v2 current edit <span style={{ fontSize: 10, backgroundColor: '#0f172a', color: 'white', padding: '1px 6px', borderRadius: 4, marginLeft: 6 }}>current</span></div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>green eyes</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>11:44 AM</div>
                </div>
              </div>
            </div>

            {/* Code Panel */}
            <div style={{ 
              backgroundColor: '#0b1220', 
              borderRadius: 20, 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{ height: 48, borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
                <div style={{ display: 'flex', gap: 24 }}>
                  <span style={{ color: 'white', fontSize: 13, fontWeight: 600, borderBottom: '2px solid white', height: 48, display: 'flex', alignItems: 'center' }}>Component</span>
                  <span style={{ color: '#64748b', fontSize: 13, fontWeight: 500, height: 48, display: 'flex', alignItems: 'center' }}>Usage</span>
                </div>
                <div style={{ 
                  padding: '6px 12px', 
                  borderRadius: 8, 
                  border: '1px solid #334155', 
                  color: 'white', 
                  fontSize: 12,
                  transform: frame > 245 && frame < 260 ? 'scale(0.92)' : 'scale(1)',
                  transition: 'transform 0.1s'
                }}>Copy</div>
              </div>
              <div style={{ padding: '20px', fontFamily: 'monospace', fontSize: 13, lineHeight: '1.6', color: '#94a3b8', overflowY: 'auto' }}>
                <div><span style={{ color: '#c084fc' }}>import</span> {'{ AbsoluteFill, spring, interpolate, useCurrentFrame }'} <span style={{ color: '#c084fc' }}>from</span> <span style={{ color: '#4ade80' }}>'remotion'</span>;</div>
                <div style={{ color: '#475569', marginTop: 12 }}>// HELPER COMPONENTS (No export keyword)</div>
                <div><span style={{ color: '#c084fc' }}>const</span> <span style={{ color: '#fbbf24' }}>UnderwaterBackground</span> = () =&gt; {'{'}</div>
                <div style={{ paddingLeft: 20 }}><span style={{ color: '#c084fc' }}>const</span> frame = <span style={{ color: '#fbbf24' }}>useCurrentFrame</span>();</div>
                <div style={{ paddingLeft: 20 }}><span style={{ color: '#c084fc' }}>const</span> drift = <span style={{ color: '#fbbf24' }}>interpolate</span>(frame, [0, 300], [0, 100]);</div>
                <div style={{ paddingLeft: 20, marginTop: 8 }}><span style={{ color: '#c084fc' }}>return</span> (</div>
                <div style={{ paddingLeft: 40 }}>&lt;<span style={{ color: '#fbbf24' }}>AbsoluteFill</span> style={'{'}{'{'} backgroundColor: <span style={{ color: '#4ade80' }}>'#0d9488'</span> {'}'}{'}'}&gt;</div>
                <div style={{ paddingLeft: 60 }}>&lt;<span style={{ color: '#fbbf24' }}>div</span> style={'{'}{'{'} transform: <span style={{ color: '#4ade80' }}>`translateX(${'{'}drift{'}'}px)`</span> {'}'}{'}'} /&gt;</div>
                <div style={{ paddingLeft: 40 }}>&lt;/<span style={{ color: '#fbbf24' }}>AbsoluteFill</span>&gt;</div>
                <div style={{ paddingLeft: 20 }}>);</div>
                <div>{'}'};</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default VemotionRecreation;
